import {InboundBridgeBucketRecord} from './index';
import {Box, Container, Header, MixedLineBarChart, MixedLineBarChartProps, Spinner} from '@amzn/awsui-components-react';
import React from 'react';
import * as awsui from '@amzn/awsui-design-tokens/polaris';

const CHART_DESCRIPTION = 'Sequential progression showing cumulative plan-to-plan changes.';

export function buildTooltipContent(
    series: MixedLineBarChartProps.ChartSeries<string>,
    x: string,
    y: number,
    deltaMap: Record<string, number>
): {key: string; value: string} {
    if (series.title === 'Base') {
        return {key: '\u200B', value: '\u200B'};
    }
    const delta = deltaMap[x];
    if (delta === undefined) {
        return {key: 'Plan', value: y.toLocaleString()};
    }
    const sign = delta > 0 ? '+' : '';
    return {key: delta >= 0 ? 'Increase' : 'Decrease', value: `${sign}${delta.toLocaleString()}`};
}

export function InboundBridgeWaterfallChart({data, isLoading}: {data: InboundBridgeBucketRecord[]; isLoading: boolean}) {
    if (isLoading) {
        return (
            <Container header={<Header variant="h2">Aggregated Plan-to-Plan Changes</Header>}>
                <Box textAlign="center" color="inherit">
                    <Spinner size="large" />
                </Box>
            </Container>
        );
    }

    const {baseSeries, valueSeries, deltaMap} = buildSeries(data);

    return (
        <Container
            header={
                <Header variant="h2" description={CHART_DESCRIPTION}>
                    Aggregated Plan-to-Plan Changes
                </Header>
            }
        >
            <MixedLineBarChart
                series={[baseSeries, valueSeries]}
                xScaleType="categorical"
                yScaleType="linear"
                xTitle="Bucket"
                yTitle="Units"
                stackedBars
                hideFilter
                hideLegend
                height={400}
                detailPopoverSeriesContent={({series, x, y}) => buildTooltipContent(series, x as string, y, deltaMap)}
                empty={
                    <Box color="inherit" textAlign="center">
                        <Box fontWeight="bold">No data available</Box>
                        <Box color="inherit" variant="p">
                            There is no data for the selected filters.
                        </Box>
                    </Box>
                }
                noMatch={
                    <Box color="inherit" textAlign="center">
                        <Box fontWeight="bold">No matching data</Box>
                        <Box color="inherit" variant="p">
                            There is no data matching your filter criteria.
                        </Box>
                    </Box>
                }
            />
        </Container>
    );
}

export function buildSeries(records: InboundBridgeBucketRecord[]): {
    baseSeries: MixedLineBarChartProps.BarDataSeries<string>;
    valueSeries: MixedLineBarChartProps.BarDataSeries<string>;
    deltaMap: Record<string, number>;
} {
    const empty = {
        baseSeries: {title: 'Base', type: 'bar' as const, color: 'transparent', data: []},
        valueSeries: {title: 'Value', type: 'bar' as const, data: []},
        deltaMap: {},
    };

    if (records.length === 0) return empty;

    const chartData = transformData(records);
    const {bucketNames, baseSeries, stackedSeries, deltaSeries} = chartData;
    const lastIndex = bucketNames.length - 1;

    // deltaMap only contains middle bars (not first/last edges)
    const deltaMap: Record<string, number> = {};
    bucketNames.forEach((name, i) => {
        if (i !== 0 && i !== lastIndex) {
            deltaMap[name] = deltaSeries[i];
        }
    });

    return {
        baseSeries: {
            title: 'Base',
            type: 'bar',
            color: 'transparent',
            data: bucketNames.map((name, i) => ({x: name, y: baseSeries[i]})),
        },
        valueSeries: {
            title: 'Value',
            type: 'bar',
            data: bucketNames.map((name, i) => ({x: name, y: stackedSeries[i], color: awsui.colorChartsBlue1300})),
        },
        deltaMap,
    };
}

export function transformData(records: InboundBridgeBucketRecord[]): ChartData {
    if (records.length === 0) {
        return {bucketNames: [], baseSeries: [], stackedSeries: [], deltaSeries: [], valueSeries: []};
    }

    const sortedData = [...records].sort((a, b) => a.seqId - b.seqId);
    const withNewPlan = [
        ...sortedData,
        {
            ...sortedData[sortedData.length - 1],
            seqId: 99999,
            name: 'New Plan',
            value: sortedData[sortedData.length - 1].value,
        },
    ];

    const transformed = withNewPlan.map((record, index, array) => {
        if (index === 0) {
            return {...record, base: 0, stacked: record.value, delta: record.value};
        } else if (index === array.length - 1) {
            return {...record, base: 0, stacked: record.value, delta: 0};
        } else {
            const prevValue = array[index - 1]?.value ?? 0;
            const currentValue = record.value ?? 0;
            const delta = currentValue - prevValue;
            return {
                ...record,
                base: delta >= 0 ? prevValue : currentValue,
                stacked: Math.abs(delta),
                delta,
            };
        }
    });

    return {
        bucketNames: transformed.map((r) => r.name!),
        baseSeries: transformed.map((r) => r.base!),
        stackedSeries: transformed.map((r) => r.stacked!),
        deltaSeries: transformed.map((r) => r.delta!),
        valueSeries: transformed.map((r) => r.value!),
    };
}

export type ChartData = {
    bucketNames: string[];
    baseSeries: number[];
    stackedSeries: number[];
    deltaSeries: number[];
    valueSeries: number[];
};
